<template>
  <div
    v-if="flashcard.id"
    class="answered-flashcard-container"
  >
    <div class="grid-view">
      <div class="column-one">
        <correctness-header
          :correct="correct"
        ></correctness-header>
        <icon-button
          v-if="flashcard.keyTakeaway"
          class="two-column"
          label-html="Key Takeaway"
          icon-name="trophy"
          variant="key-takeaway"
          @click="scrollTo(1,'key-takeaway')"
        ></icon-button>
        <span class="two-column">
          <hr />
        </span>
        <component
          :is="answerSelectorType"
          class="two-column"
          data-test="answer-selector"
          :answered="true"
          :correct="correct"
          :flashcard="flashcard"
          :flashcard-state="flashcardState"
          :answer-ids="answerIds"
          :raw-answer-data="rawAnswerData"
          :left-column="true"
          @click="handleAnswerClick"
        />
        <span class="two-column">
          <hr />
        </span>
        <icon-button
          class="two-column"
          label-html="Explanation"
          variant="rationale"
          icon-name="rationale"
          @click="scrollTo(1, 'rationale', 'start')"
        ></icon-button>
        <span class="two-column spacer"></span>
        <exam-tip
          v-if="flashcard.examTip"
          class="two-column left-column-fixed"
        >
          <div
            v-html="flashcard.examTip"
          ></div>
        </exam-tip>
      </div>
      <div class="column-two">
        <tab-menu
          :active-tab="activeTab"
          :discussion-post-count="discussionPostCount"
          :show-explore-tab="showExploreTab"
          :tab-labels="tabLabels"
          @tab-clicked="activeTabIndex = $event"
        >
        </tab-menu>
        <div
          v-if="activeTab === 'Question'"
          class="content-container"
        >
          <question-stem
            :flashcard="flashcard"
          />
          <component
            :is="flashcardType"
            :class="{
              'show-corrected-answers': showCorrectedAnswers,
              'one-column': !showQuestionOnDesktop,
            }"
            :answered="true"
            :answer-ids="answerIds"
            :correct="correct"
            :flashcard="flashcard"
            :flashcard-state="flashcardState"
            :raw-answer-data="rawAnswerData"
            @click="handleAnswerClick"
          />
          <asset-tray
            id="assetTray"
            class="asset-tray"
            :class="{ 'hide-asset-tray': !showAssetTray }"
            :flashcard="flashcard"
          ></asset-tray>
          <mobile-asset-tray-activator
            class="one-column"
            :flashcard="flashcard"
          >
          </mobile-asset-tray-activator>
        </div>
        <div
          v-if="activeTab === 'Explanation'"
          class="content-container"
        >
          <div class="column">
            <flashcard-metadata
              :flashcard="flashcard"
            ></flashcard-metadata>
            <key-takeaway
              v-if="flashcard.keyTakeaway"
              id="key-takeaway"
            >
              <div
                v-html="flashcard.keyTakeaway"
              ></div>
            </key-takeaway>
            <exam-tip
              v-if="flashcard.examTip"
              class="one-column"
            >
              <div
                v-html="flashcard.examTip"
              ></div>
            </exam-tip>
            <rationale-list
              v-if="showRationaleList"
              data-test="rationale"
              :flashcard="flashcard"
              :answer-ids="answerIds"
              :correct="correct"
              :raw-answer-data="rawAnswerData"
              :is-sata="allowMultipleAnswers"
            ></rationale-list>
            <main-rationale
              id="rationale"
              :class="{ 'top-border': showRationaleList}"
              :rationale="flashcard.rationale"
            ></main-rationale>
            <viewed-explanation-observer
              :flashcard="flashcard"
              :category="category"
              :correct="correct"
            ></viewed-explanation-observer>
            <five-star-rating
              :asset="flashcard"
              :category="category"
              :rateable-type="'V3Flashcard'"
            ></five-star-rating>
          </div>
        </div>

        <div
          v-if="activeTab === 'Discussions'"
          class="content-container"
        >
          <div class="column">
            <discussion-list
              class="discussion-list"
              data-test="discussion-list"
              :category="category"
              :flashcard="flashcard"
              :user="$store.getters['session/currentUser']"
            />
          </div>
        </div>
        <div
          v-if="activeTab === 'Explore' && showExploreTab"
          class="content-container associations-container"
        >
          <asset-list
            :assets="assets"
            :asset-associations="assetAssociations"
          ></asset-list>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// components
import AssetList from '@components/flashcards-v2/fragments/AssetList.vue';
import AssetTray from '@components/flashcards-v2/fragments/AssetTray';
import CorrectnessHeader from '@components/flashcards-v2/fragments/CorrectnessHeader.vue';
import DiscussionList from '@components/discussions-v2/DiscussionList.vue';
import ExamTip from './fragments/ExamTip.vue';
import FiveStarRating from '@components/singletons/FiveStarRating.vue';
import FlashcardMetadata from '@components/flashcards-v2/fragments/FlashcardMetadata.vue';
import FractionalNumericEntry from '@components/flashcards-v2/types/FractionalNumericEntry';
import IconButton from '@components/flashcards-v2/fragments/IconButton.vue';
import KeyTakeaway from '@components/flashcards-v2/fragments/KeyTakeaway.vue';
import MainRationale from '@components/flashcards-v2/fragments/MainRationale.vue';
import MobileAssetTrayActivator from '@components/flashcards-v2/fragments/MobileAssetTrayActivator.vue';
import MultipleChoice from '@components/flashcards-v2/types/MultipleChoice';
import NumericEntry from '@components/flashcards-v2/types/NumericEntry';
import QuestionStem from '@components/flashcards-v2/fragments/QuestionStem';
import RationaleList from '@components/flashcards-v2/fragments/RationaleList.vue';
import TabMenu from '@components/flashcards-v2/fragments/TabMenu.vue';
import ViewedExplanationObserver from '@components/flashcards-v2/fragments/ViewedExplanationObserver.vue';
// mixins
import AssetTrayMixin from '@/mixins/asset-tray';
import DiscussionsMixin from '@/mixins/discussions';
import MathMlMixin from '@/mixins/mathml';
import MultipleChoiceMixin from '@/mixins/multipleChoice.js';
// utils
import { asyncImport } from '@/utils';
import { flashcardType } from '@/utils/flashcards';
// lazy load components
const MatrixGrid = () => asyncImport(import(/* webpackChunkName: "matrixGrid" */ '@components/flashcards-v2/types/MatrixGrid'));
const MatrixGridSelector = () => asyncImport(import(/* webpackChunkName: "matrixGridSelector" */ '@components/flashcards-v2/answer-selector-types/MatrixGridSelector'));
const OrderedResponse = () => asyncImport(import(/* webpackChunkName: "orderedResponse" */ '@components/flashcards-v2/answered-types/AnsweredOrderedResponse'));
const OrderedResponseSelector = () => asyncImport(import(/* webpackChunkName: "orderedResponseSelector" */ '@components/flashcards-v2/answer-selector-types/OrderedResponseSelector'));
const Bowtie = () => asyncImport(import(/* webpackChunkName: "bowtie" */ '@components/flashcards-v2/answered-types/AnsweredBowtie'));
const BowtieSelector = () => asyncImport(import(/* webpackChunkName: "bowtieSelector" */ '@components/flashcards-v2/answer-selector-types/BowtieSelector'));
const ClozeDropdownSelector = () => asyncImport(import(/* webpackChunkName: "clozeDropDownSelector" */ '@components/flashcards-v2/answer-selector-types/ClozeDropdownSelector'));
const ClozeDropdown = () => asyncImport(import(/* webpackChunkName: "clozeDropDown" */ '@components/flashcards-v2/answered-types/AnsweredClozeDropdown'));
const DragAndDrop = () => asyncImport(import(/* webpackChunkName: "dragAndDrop" */ '@components/flashcards-v2/answered-types/AnsweredDragAndDrop'));
const DragAndDropSelector = () => asyncImport(import(/* webpackChunkName: "dragAndDropSelector" */ '@components/flashcards-v2/answer-selector-types/DragAndDropSelector'));
const Hotspot = () => asyncImport(import(/* webpackChunkName: "dragAndDrop" */ '@components/flashcards-v2/answered-types/AnsweredHotspot'));
const HotspotSelector = () => asyncImport(import(/* webpackChunkName: "hotSpotSelector" */ '@components/flashcards-v2/answer-selector-types/HotspotSelector'));
const Highlight = () => asyncImport(import(/* webpackChunkName: "highlight" */ '@components/flashcards-v2/answered-types/AnsweredHighlight'));
const HighlightSelector = () => asyncImport(import(/* webpackChunkName: "highlightSelector" */ '@components/flashcards-v2/answer-selector-types/HighlightSelector'));
const BlankTextCompletion = () => asyncImport(import(/* webpackChunkName: "blankTextCompletion" */ '@components/flashcards-v2/answered-types/AnsweredBlankTextCompletion'));
const BlankTextCompletionSelector = () => asyncImport(import(/* webpackChunkName: "blankTextCompletionSelector" */ '@components/flashcards-v2/answer-selector-types/BlankTextCompletionSelector'));

const HAS_ANSWER_SELECTOR_TYPE = ['hotspot', 'ordered-response', 'matrix-grid', 'bowtie', 'drag-and-drop', , 'cloze-dropdown', 'highlight', 'blank-text-completion'];
const TAB_LABELS = ['Question', 'Explanation', 'Discussions', 'Explore'];

export default {
  components: {
    AssetList,
    AssetTray,
    BlankTextCompletion,
    BlankTextCompletionSelector,
    Bowtie,
    BowtieSelector,
    CorrectnessHeader,
    ClozeDropdown,
    ClozeDropdownSelector,
    DiscussionList,
    DragAndDrop,
    DragAndDropSelector,
    ExamTip,
    FiveStarRating,
    FlashcardMetadata,
    FractionalNumericEntry,
    Highlight,
    HighlightSelector,
    Hotspot,
    HotspotSelector,
    IconButton,
    KeyTakeaway,
    MainRationale,
    MatrixGrid,
    MatrixGridSelector,
    MobileAssetTrayActivator,
    MultipleChoice,
    NumericEntry,
    OrderedResponse,
    OrderedResponseSelector,
    RationaleList,
    QuestionStem,
    TabMenu,
    ViewedExplanationObserver,
  },
  mixins: [DiscussionsMixin, AssetTrayMixin, MultipleChoiceMixin, MathMlMixin],
  props: {
    answerIds: {
      type: Array,
    },
    correct: {
      type: Boolean,
      required: true,
    },
    flashcard: {
      type: Object,
      required: true,
    },
    category: {
      type: Object,
      required: true,
    },
    rawAnswerData: {
      type: Object,
    },
  },
  data() {
    return {
      activeTabIndex: 1,
      activeRationale: '',
      tabLabels: TAB_LABELS,
    };
  },
  computed: {
    activeTab() {
      return this.tabLabels[this.activeTabIndex];
    },
    answerSelectorType() {
      if (HAS_ANSWER_SELECTOR_TYPE.includes(this.flashcardType)) {
        return this.flashcardType + '-selector';
      }
      return this.flashcardType;
    },
    assetAssociations() {
      return this.flashcard.assetAssociations
        .filter(a => a.assetAContext === 'rationale')
        || [];
    },
    assets() {
      return this.assetAssociations
        .map(a => {
          const id = a.assetBId;
          const type = a.assetBType;
          let asset;
          if (type === 'Topic') {
            asset = this.$store.getters['topics/topics'][id];
          } else if (type === 'Attachment') {
            asset = this.$store.getters['attachments/get'][id];
          }
          return {
            ...asset,
            assetType: type,
          };
        });
    },
    firstSideNavigation() {
      if (this.flashcard.keyTakeaway) return 'key-takeaway';
      if (this.flashcard.type === 'Multiple Choice' || this.flashcard.type === 'SATA') {
        const firstAnswer = this.answers[0];
        return `answer-rationale-${firstAnswer.id}`;
      };
      return 'rationale';
    },
    flashcardState() {
      const flashcardStateKey = `${this.flashcard.id}-category-${this.category.id}`;
      return this.$store.getters['flashcardStates/flashcardStates'][flashcardStateKey];
    },
    flashcardType() {
      return flashcardType(this.flashcard);
    },
    allowMultipleAnswers() {
      if (this.flashcard.type === 'SATA') {
        return true;
      }
      if (this.flashcard.type === 'Multiple Choice') {
        return this.flashcard.answers.filter(a => a.correct).length > 1;
      }
      return false;
    },
    highlightedAnswerId() {
      const regex = /answer-rationale-(\d+)/;
      const match = this.activeRationale.match(regex);
      return match ? Number(match[1]) : null;
    },
    showCorrectedAnswers() {
      if (this.flashcardType === 'matrix-grid') {
        return true;
      }
      return false;
    },
    showQuestionOnDesktop() {
      const types = ['hotspot', 'ordered-response', 'matrix-grid', 'bowtie', 'cloze-dropdown', 'drag-and-drop', 'highlight', 'blank-text-completion'];
      return types.includes(this.flashcardType);
    },
    showRationaleList() {
      return this.flashcard.type === 'Multiple Choice' || this.flashcard.type === 'SATA';
    },
    showExploreTab() {
      return this.assetAssociations.length > 0;
    },
  },
  watch: {
    activeTabIndex() {
      if (this.activeTabIndex !== 1) {
        this.activeRationale = this.firstSideNavigation;
      }
    },
    flashcard() {
      this.activeRationale = this.firstSideNavigation;
    },
  },
  mounted() {
    this.activeRationale = this.firstSideNavigation;
  },
  methods: {
    scrollTo(activeTab, selector, block='center') {
      this.activeRationale = selector;
      this.activeTabIndex = activeTab;
      if (!selector) {
        return;
      }

      this.$nextTick(() => {
        const el = document.querySelector('#' + selector);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block,
          });
        }
      });
    },
    handleAnswerClick(answerId) {
      this.scrollTo(1, `answer-rationale-${answerId}`);
    },
  },
};
</script>

<style lang="scss" scoped>
  .content-container {
    background-color: white;
    border: 1px solid #D0D5DD;
    border-radius: rem-calc(12);
    padding: rem-calc(12);
    margin: 1rem;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: rem-calc(40);

    > * {
      margin: 0 auto;
      width: 100%;
    }

    &.associations-container {
      padding: 0;
      gap: 0;

      > * {
        margin: 0;
        width: unset;
      }
    }

    .asset-association-item {
      padding: rem-calc(12);
      border-bottom: 1px solid #D0D5DD;
    }
  }

  .asset-tray.hide-asset-tray {
    display: none;
  }

  // deep selectors
  #rationale {
    /deep/ .icon-slot {
      margin-top: 2px;
    }
}

  @include breakpoint(medium) {
    .two-column {
      display: none;
      visibility: hidden;
    }
  }

  @include breakpoint(desktop) {

    hr {
      margin-top: rem-calc(16);
      margin-bottom: rem-calc(16);
      border: none;
      border-top: 1px solid #E3E8EF;
    }

    .spacer {
      flex-grow: 1;
    }

    .content-container {
      padding: 4rem 7rem;
      margin-left: 0;
      margin-right: 0;
      min-height: calc(100vh - 27rem);
      height: calc(100vh - 27rem);
      overflow-y: auto;

      > * {
        max-width: 70ch;
      }

      &.associations-container {
        min-height: calc(100vh - 19rem);
        height: calc(100vh - 19rem);
        align-items: center;

        > * {
          max-width: unset;
          width: 100%;
        }
      }
    }

    .one-column {
      display: none;
      visibility: hidden;

      &.show-corrected-answers {
        display: block;
        visibility: visible;
      }
    }

    .grid-view {
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      gap: rem-calc(32);
      border: none;
      margin: 2rem 2rem 0;
      padding: 0;
      background-color: transparent;
      height: calc(100vh - 12.5rem);

      .column-one {
        grid-column: 1 / 4;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        padding-right: rem-calc(16);
        padding-bottom: rem-calc(16);
      }

      .column-two {
        grid-column: 5 / 13;
      }
    }

    .asset-tray.hide-asset-tray {
      display: block;
    }
  }

  .top-border {
    border-top: 1px solid #E3E8EF;
  }
</style>
